import {Component, Input, OnInit} from '@angular/core';
import {
  ClassDto,
  ClassFilterDto,
  EntityServiceClient,
  RawMaterialDto,
  ResultDto
} from '@sharedcommon/rest/data.restservice';
import {UserService} from '@sharedcommon/services/user.service';
import {map} from 'rxjs/operators';
import {Constants} from '@sharedcommon/util/constants.util';
import {FunctionUtils} from '@sharedcommon/util/functions.util';
import {UserResultPref, UserResultPrefValueType} from '@sharedcommon/model/user.result-pref.model';
import {ResultSettingsService} from '@usercommon/service/result-settings.service';
import {TranslateService} from '@ngx-translate/core';
import {MatrixDto} from '@sharedrest/equation.restservice';
import {TechnicalDocService} from '@sharedcommon/services/technical-doc.service';
import {PrimeTableDataModel} from '@sharedcommon/model/prime-table-data.model';
import {PrimengTableCols} from '@sharedcommon/interfaces/primeng-table-cols';
import { lastValueFrom } from 'rxjs';
import { SampleDto } from '@sharedcommon/model/sample/sample.model';
import { RawmaterialOrSampleService } from '@usercommon/service/rawmaterial-or-sample.service';
import { GenericResultDetailFilterModel } from '@sharedcommon/model/generic-result-detail-filter.model';

@Component({
  selector: 'neq-results-table',
  templateUrl: './results-tables.component.html',
  styleUrls: [ './results-tables.component.scss']
})
export class NeqResultsTableComponent implements OnInit {

  userActivity: string;
  userEntity: string;
  selectedClass: ClassDto;

  /**
   * Raw material or sample ID
   */
  @Input()
  productId: string;
  @Input()
  calculationId: string;
  @Input()
  userClassKey: string;
  @Input()
  matrices: MatrixDto[];
  @Input()
  rawMaterialOrSample: RawMaterialDto|SampleDto;

  showUnit: boolean;
  resultPrefs: UserResultPref[];

  /**
   * Data to be shown in the primeng table
   */
  tableData: PrimeTableDataModel;

  cols: PrimengTableCols[] = [
    {field: 'nutrientLabel', header: 'label.common.nutrient', position: 'left'},
    {field: 'nutrientUnit', header: 'label.common.unit', position: 'center'}
  ];

  constructor(
    private readonly userService: UserService,
    private readonly rawMatOrSampleService: RawmaterialOrSampleService,
    private readonly entityService: EntityServiceClient,
    private readonly resultSettingsService: ResultSettingsService,
    private readonly translate: TranslateService,
    private readonly technicalDocService: TechnicalDocService) {
  }

  ngOnInit() {

    this.resultPrefs = this.resultSettingsService.getResultPrefs();

    this.userActivity = this.userService.getActivity();
    this.userEntity = this.userService.getEntity();

    this.showUnit = this.userService.getMetaValue(Constants.USER_META_RESULT_SHOW_UNIT) !== 'false';

    const userMetaClassSelected = this.userService.getMetaValue(this.userClassKey);
    if (FunctionUtils.isDefined(userMetaClassSelected)) {
      const urnValue = userMetaClassSelected;
      // get class code
      const classCode = urnValue.substring(urnValue.lastIndexOf(':') + 1, urnValue.length);
      const filterClass = new ClassFilterDto();
      filterClass.code = classCode;
      filterClass.textMatchMode = 'STRICT';
      filterClass.count = 1;
      this.entityService.classesFetch(this.userEntity, filterClass).subscribe((response) => {
        if (response.data && response.data.length === 1) {
          this.onSelectClass(response.data[0]);
        }
        return response.data;
      });
    }
    this.refreshTablePrimeng();
  }

  settingsChange(): void {
    this.showUnit = this.userService.getMetaValue(Constants.USER_META_RESULT_SHOW_UNIT) !== 'false';
    this.resultPrefs = this.resultSettingsService.getResultPrefs();
    this.refreshTablePrimeng();
  }

  fetchBaseClasses = (search, page) => {
    const filter = new ClassFilterDto();
    filter.page = page;
    if (FunctionUtils.isDefined(search)) {
      filter.label = search;
    }
    filter.count = 10;
    filter.sort = [];
    return lastValueFrom(
      this.entityService
        .classesFetch(this.userEntity, filter)
        .pipe(map(result => result.data))
    );
  };

  getValueClass(result: ResultDto): string {
    const valTolerancePercent = this.userService.getMetaValue(Constants.USER_META_RESULT_TOLERANCE_PERCENT);
    if (FunctionUtils.isDefined(valTolerancePercent) && this.resultPrefs.length > 2) {
      const tolerancePercent = Number(valTolerancePercent);
      const col1 = result.values[0];
      const col2 = result.values[1];

      if (FunctionUtils.isDefined(col1) && FunctionUtils.isDefined(col2)) {
        const tolerance = col1 * tolerancePercent / 100;
        if (((col1 - tolerance) > col2) || ((col1 + tolerance) < col2)) {
          return 'tolerance';
        }
      }
    }
    return '';
  }

  getColumnValue(resultPrefs: UserResultPref): string {
    let columnTitle: string;

    if (resultPrefs.rawMaterialOrSample.id === Constants.USER_RESULT_CURRENT_RAWMATERIAL_ID) {
      columnTitle = (this.rawMaterialOrSample) ? this.rawMaterialOrSample.code : '';
    } else {
      columnTitle = resultPrefs.rawMaterialOrSample.code;
    }
    columnTitle = columnTitle.concat('<br/>');
    // check if user has multiple matrices
    if (this.matrices && this.matrices.length > 1) {
      columnTitle = columnTitle.concat(resultPrefs.matrix.code + ' ');
    }

    let translateKey: string;
    switch (resultPrefs.valueType) {
      case UserResultPrefValueType.CALCULATED:
        translateKey = 'label.user.rawmaterials.result.calculated.short';
        break;
      case UserResultPrefValueType.SAVED:
        translateKey = 'label.user.rawmaterials.result.saved.short';
        break;
      case UserResultPrefValueType.STANDARD:
        translateKey = 'label.user.rawmaterials.result.standard.short';
        break;

    }
    return columnTitle.concat(this.translate.instant(translateKey));
  }

  getColumnTitle(resultPrefs: UserResultPref): string {
    if (this.rawMaterialOrSample && resultPrefs.rawMaterialOrSample.id === Constants.USER_RESULT_CURRENT_RAWMATERIAL_ID) {
      return this.rawMaterialOrSample.label + ' ' + resultPrefs.matrix.label;
    } else {
      return resultPrefs.rawMaterialOrSample.label + ' ' + resultPrefs.matrix.label;
    }
  }

  async refreshTablePrimeng() {
    const data = new PrimeTableDataModel();

    if (!this.selectedClass) {
      data.paginate = false;
      data.results = [];
    } else {
      const isSampleMode = !this.userService.isFeedOperatingMode();
      const resultDetails = this.resultPrefs.map((pref) => {
        return new GenericResultDetailFilterModel(pref.id, pref.rawMaterialOrSample.id, pref.matrix.code, pref.valueType, isSampleMode);
      });
      let metaClassNutIds: string[];
      let classNutIds: string[];
      const arrayIds: string[] = [this.selectedClass.id];
      if (this.selectedClass.meta) {
        metaClassNutIds = arrayIds;
        classNutIds = [];
      } else {
        classNutIds = arrayIds;
        metaClassNutIds = [];
      }
      const fetchResult = await lastValueFrom(
        this.rawMatOrSampleService.retrieveResults(
          this.userEntity, this.productId, classNutIds, metaClassNutIds, resultDetails, this.calculationId
        )
      );
      if (fetchResult) {
        data.results = fetchResult;
        data.paginate = false;
      }
    }
    this.tableData =  data;
  }

  async onSelectClass(selectClass: ClassDto) {
    this.selectedClass = selectClass;
    this.refreshTablePrimeng();
    // delete previous value
    this.userService.removeMeta(this.userClassKey);
    if (FunctionUtils.isDefined(selectClass)) {

      const metaValue = 'urn:neovia:class:' + selectClass.code;

      this.userService.saveMeta(this.userClassKey, metaValue);
    }
    this.refreshTablePrimeng();
  }

  getNutrientTechnicalDocUrl(result: ResultDto) {
    if (result?.technicalDocAvailable) {
      this.technicalDocService.getNutrientTechnicalDoc(`${result.nutrientCode}.${result.technicalDocFormat}`);
    }
  }

  isOutOfTolerance(row: ResultDto, colId: number): boolean {
    if (colId !== 1 || !row || row.values.length < 2 || row.values[0] == null || row.values[1] == null) {
      return false;
    }
    const tolerance = this.userService.getMetaValue(Constants.USER_META_RESULT_TOLERANCE_PERCENT);
    if (!tolerance || Number.isNaN(+tolerance)) {
      return false;
    }
    const d = Math.abs(row.values[0] * +tolerance / 100);
    return (row.values[1] < row.values[0] - d) || (row.values[1] > row.values[0] + d);
  }

  /**
   * CHeck if the row has a value which has reached the floor or the ceil limits and is not standard
   * @param row the row where we want to know if the ceiling limits are reached
   * @param index the index of the value
   * @returns true if ceiling limits reached and is not standard
   */
  isCeilOrFloorReachedAndNotStandard(row: ResultDto, index: number): boolean {
    return row.allCeilOrFloorReached && row.standardFlags && row.allCeilOrFloorReached[index] && !row.standardFlags[index];
  }

  /**
   * Get the tool tip for the given row and the given index of values
   * @param row the row where we want to get the tool tip message
   * @param indexOfValues the index of the value
   * @returns the corresponding tool tip
   */
  getValueTooltip(row: ResultDto, indexOfValues: number): string {
    if (!this.isCeilOrFloorReachedAndNotStandard(row, indexOfValues)) {
      return '' ;
    }
    const ceilValue = row.ceilValues[indexOfValues] ;
    const floorValue = row.floorValues[indexOfValues] ;
    const decimal = row.decimal ;
    const shownResult = this.getNumberFromRow(row, indexOfValues) ;
    const valueLine = this.translate.instant('msg.user.rawmaterial.calculated-result') + " : " + shownResult ;
    const ceilValueLine = this.translate.instant('label.common.ceiling-value') + " : " + (this.formatNumber(ceilValue, decimal) || '-') ;
    const floorValueLine = this.translate.instant('label.common.floor-value') + " : " + (this.formatNumber(floorValue, decimal) || '-') ;
    return valueLine
            + '\n' + ceilValueLine
            + '\n' + floorValueLine ;
  }

  public refresh() {
    this.refreshTablePrimeng();
  }

  formatNumber(value: number, decimal: number): any {
    if (FunctionUtils.isDefined(value) && FunctionUtils.isDefined(decimal)) {
      return value.toFixed(decimal);
    } else {
      return value;
    }
  }

  /**
   * Get the value to display
   * @param row the where on which we want to display the value
   * @param indexOfValue the index of the value
   * @returns the value to display
   */
  getNumberFromRow(row: ResultDto, indexOfValue: number): any {
    const value = row.values[indexOfValue] ;
    const cappedVal = row.cappedValues[indexOfValue] ;
    const standard = row.standardFlags[indexOfValue] ;
    const limitableRefVal = row.limitableValuesRefVal[indexOfValue] ;
    const ceilOrFloorReached = row.allCeilOrFloorReached[indexOfValue] ;
    const valueToShow = (standard || ceilOrFloorReached || !limitableRefVal || FunctionUtils.isNotDefined(cappedVal)) ? value : cappedVal ; ;
    const decimal = row.decimal ;
    return this.formatNumber(valueToShow, decimal) ;
  }
}
